import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  BarChart3,
  BellRing,
  CircleDollarSign,
  CreditCard,
  Info,
  Loader2,
  Percent,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  ShoppingCart,
  Wallet,
} from 'lucide-react';
import DateRangeFilter from '../../components/DateRangeFilter';
import TrendChart, { type TrendSeries } from '../../components/TrendChart';
import { useAppOptions } from '../../hooks/useAppOptions';
import { payReportApi } from '../../utils/api';
import {
  daysAgo,
  formatCompact,
  formatMoney,
  formatRate,
  rangeDays,
  today,
} from '../../utils/format';
import type {
  PayReportChannelItem,
  PayReportGranularity,
  PayReportOverview,
  PayReportTrend,
} from '../../types/api';

const SERIES_COLORS = ['#34d399', '#60a5fa', '#c084fc', '#fbbf24', '#f87171', '#22d3ee'];

type MetricKey = 'success_amount' | 'order_count' | 'success_count';

const METRICS: { key: MetricKey; label: string }[] = [
  { key: 'success_amount', label: 'Amount' },
  { key: 'order_count', label: 'Orders' },
  { key: 'success_count', label: 'Paid' },
];

const GRANULARITIES: { key: PayReportGranularity; label: string }[] = [
  { key: 'hour', label: 'Hour' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

function formatBucket(label: string, gran: PayReportGranularity): string {
  if (gran === 'hour') return label.slice(11);
  if (gran === 'day') return label.slice(5);
  return label;
}

function formatCount(value: number): string {
  return Math.round(value).toLocaleString();
}

function formatAxisCount(value: number): string {
  if (value >= 1_000_000) return `${value / 1_000_000}m`;
  if (value >= 1_000) return `${value / 1_000}k`;
  return `${Math.round(value)}`;
}

interface StatCardProps {
  label: string;
  value: string;
  hint: string;
  icon: React.ElementType;
  accent: string;
  progress?: number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, hint, icon: Icon, accent, progress }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
    <div className="flex items-center justify-between gap-3">
      <span className="text-zinc-400 text-sm">{label}</span>
      <div className={`p-2 rounded-lg ${accent}`}>
        <Icon size={18} />
      </div>
    </div>
    <div className="mt-3 text-2xl font-bold text-white truncate" title={value}>
      {value}
    </div>
    <div className="mt-1 text-xs text-zinc-500">{hint}</div>
    {progress !== undefined && (
      <div className="mt-3 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-400 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
        />
      </div>
    )}
  </div>
);

const PayReportPage: React.FC = () => {
  const { apps } = useAppOptions();

  const [appId, setAppId] = useState('');
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today());
  const [currency, setCurrency] = useState('');
  const [gran, setGran] = useState<PayReportGranularity>('day');
  const [metric, setMetric] = useState<MetricKey>('success_amount');

  const [overview, setOverview] = useState<PayReportOverview | null>(null);
  const [trend, setTrend] = useState<PayReportTrend | null>(null);
  const [channels, setChannels] = useState<PayReportChannelItem[]>([]);
  const [knownCurrencies, setKnownCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  const totalDays = rangeDays(start, end);
  const hourDisabled = totalDays > 31;

  // hour 粒度最多 31 天，超出时自动退回 day
  useEffect(() => {
    if (gran === 'hour' && hourDisabled) setGran('day');
  }, [gran, hourDisabled]);

  const fetchReport = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');

    const query = {
      app_id: appId || undefined,
      start_time: start,
      end_time: end,
      currency: currency || undefined,
    };

    const [overviewRes, trendRes, channelsRes] = await Promise.allSettled([
      payReportApi.getOverview(query),
      payReportApi.getTrend(query, gran),
      payReportApi.getChannels(query),
    ]);

    // 过滤条件快速变化时，丢弃过期请求的结果
    if (requestId !== requestIdRef.current) return;

    let nextOverview: PayReportOverview | null = null;
    let nextTrend: PayReportTrend | null = null;
    let nextChannels: PayReportChannelItem[] = [];
    let firstError = '';
    const foundCurrencies = new Set<string>();

    if (overviewRes.status === 'fulfilled') {
      const response = overviewRes.value;
      if (response.code === 1 && response.data) {
        nextOverview = response.data;
        response.data.currencies.forEach((item) => foundCurrencies.add(item.currency));
      } else {
        firstError = response.msg || 'Failed to load overview';
      }
    } else {
      firstError = 'Failed to load overview';
    }

    if (trendRes.status === 'fulfilled') {
      const response = trendRes.value;
      if (response.code === 1 && response.data) {
        nextTrend = response.data;
        response.data.currencies.forEach((item) => foundCurrencies.add(item));
      } else if (!firstError) {
        firstError = response.msg || 'Failed to load trend';
      }
    } else if (!firstError) {
      firstError = 'Failed to load trend';
    }

    if (channelsRes.status === 'fulfilled') {
      const response = channelsRes.value;
      if (response.code === 1 && response.data) {
        nextChannels = Array.isArray(response.data.items) ? response.data.items : [];
        nextChannels.forEach((item) => foundCurrencies.add(item.currency));
      } else if (!firstError) {
        firstError = response.msg || 'Failed to load channels';
      }
    } else if (!firstError) {
      firstError = 'Failed to load channels';
    }

    setOverview(nextOverview);
    setTrend(nextTrend);
    setChannels(nextChannels);

    if (foundCurrencies.size > 0) {
      setKnownCurrencies((previous) =>
        Array.from(new Set([...previous, ...Array.from(foundCurrencies)])).sort()
      );
    }

    setError(firstError);
    setLoading(false);
  }, [appId, start, end, currency, gran]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport, reloadKey]);

  const trendData = useMemo(() => {
    if (!trend || trend.points.length === 0) {
      return { labels: [] as string[], series: [] as TrendSeries[] };
    }

    const labels = Array.from(new Set(trend.points.map((point) => point.bucket)));
    const currencies =
      trend.currencies.length > 0
        ? trend.currencies
        : Array.from(new Set(trend.points.map((point) => point.currency)));

    const series: TrendSeries[] = currencies.map((code, index) => {
      const valueMap = new Map<string, number>();
      trend.points.forEach((point) => {
        if (point.currency === code) {
          valueMap.set(point.bucket, Number(point[metric]) || 0);
        }
      });
      return {
        key: code,
        label: code,
        color: SERIES_COLORS[index % SERIES_COLORS.length] ?? '#34d399',
        currency: code,
        values: labels.map((label) => valueMap.get(label) ?? 0),
      };
    });

    return { labels, series };
  }, [trend, metric]);

  const currencyTabs = useMemo(
    () => ['ALL', ...knownCurrencies],
    [knownCurrencies]
  );

  const activeCurrency = currency || 'ALL';
  const activeStats =
    overview?.currencies.find((item) => item.currency === currency) ||
    (currency ? null : overview?.currencies[0] || null);

  const valueFormatter = useCallback(
    (value: number, code?: string) =>
      metric === 'success_amount' ? formatMoney(value.toFixed(2), code) : formatCount(value),
    [metric]
  );

  const axisFormatter = useCallback(
    (value: number, code?: string) =>
      metric === 'success_amount' ? formatCompact(value, code) : formatAxisCount(value),
    [metric]
  );

  const isFirstLoading = loading && !overview && !trend;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Payment Report</h1>
          <p className="text-zinc-400 mt-1">
            Revenue, conversion and channel performance for your applications
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/applications/payment-records"
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors"
          >
            <ReceiptText size={16} />
            Payment records
          </Link>
          <button
            onClick={() => setReloadKey((key) => key + 1)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex flex-col xl:flex-row xl:items-center gap-3">
          <select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-600 transition-colors xl:min-w-[200px]"
          >
            <option value="">All applications</option>
            {apps.map((app) => (
              <option key={app.id} value={String(app.id)}>
                {app.name}
              </option>
            ))}
          </select>

          <DateRangeFilter
            start={start}
            end={end}
            onChange={(nextStart, nextEnd) => {
              setStart(nextStart);
              setEnd(nextEnd);
            }}
          />

          <div className="xl:ml-auto text-xs text-zinc-500 flex items-center gap-1.5">
            <Info size={13} />
            {overview?.range
              ? `${overview.range.start_time} → ${overview.range.end_time} (${overview.range.timezone})`
              : `${start} 00:00:00 → ${end} 23:59:59`}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 text-red-400 text-sm flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            onClick={() => setReloadKey((key) => key + 1)}
            className="px-3 py-1.5 bg-red-400/10 hover:bg-red-400/20 rounded-lg transition-colors shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Currency tabs */}
      {!isFirstLoading && knownCurrencies.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto">
          {currencyTabs.map((code) => {
            const active = activeCurrency === code;
            return (
              <button
                key={code}
                onClick={() => setCurrency(code === 'ALL' ? '' : code)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border ${
                  active
                    ? 'bg-white text-zinc-900 border-white'
                    : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white hover:border-zinc-700'
                }`}
              >
                {code === 'ALL' ? 'All currencies' : code}
              </button>
            );
          })}
        </div>
      )}

      {/* Overview */}
      {isFirstLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-[132px] bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : activeCurrency === 'ALL' ? (
        overview && overview.currencies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {overview.currencies.map((item) => (
              <button
                key={item.currency}
                onClick={() => setCurrency(item.currency)}
                className="text-left bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl p-5 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-zinc-400 bg-zinc-800 px-2 py-1 rounded-md">
                    {item.currency}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-zinc-600 group-hover:text-white transition-colors"
                  />
                </div>
                <div className="mt-4 text-2xl font-bold text-white">
                  {formatMoney(item.success_amount, item.currency)}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  successful payments · {item.order_count} orders
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-zinc-800 pt-3 text-xs">
                  <div>
                    <div className="text-zinc-500">Paid</div>
                    <div className="text-zinc-200 font-medium mt-0.5">{item.success_count}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Success</div>
                    <div className="text-emerald-400 font-medium mt-0.5">
                      {formatRate(item.success_rate)}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Avg</div>
                    <div className="text-zinc-200 font-medium mt-0.5">
                      {formatMoney(item.avg_amount, item.currency)}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm">
            No payments in this period
          </div>
        )
      ) : activeStats ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              label="Success amount"
              value={formatMoney(activeStats.success_amount, activeStats.currency)}
              hint={`${activeStats.success_count} paid of ${activeStats.order_count} orders`}
              icon={CircleDollarSign}
              accent="bg-emerald-400/10 text-emerald-400"
            />
            <StatCard
              label="Success rate"
              value={formatRate(activeStats.success_rate)}
              hint={`${activeStats.success_count} paid · ${activeStats.closed_count} closed`}
              icon={Percent}
              accent="bg-blue-400/10 text-blue-400"
              progress={activeStats.success_rate}
            />
            <StatCard
              label="Average order value"
              value={formatMoney(activeStats.avg_amount, activeStats.currency)}
              hint="success amount / paid orders"
              icon={Wallet}
              accent="bg-purple-400/10 text-purple-400"
            />
            <StatCard
              label="Refunded"
              value={`${activeStats.refund_count}`}
              hint={`${formatMoney(activeStats.refund_amount, activeStats.currency)} refunded (approx.)`}
              icon={RotateCcw}
              accent="bg-zinc-400/10 text-zinc-300"
            />
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <BreakdownItem label="Orders" value={`${activeStats.order_count}`} icon={ShoppingCart} />
              <BreakdownItem
                label="Pending"
                value={`${activeStats.pending_count}`}
                tone="text-amber-400"
              />
              <BreakdownItem label="Closed" value={`${activeStats.closed_count}`} tone="text-red-400" />
              <BreakdownItem label="Refunded" value={`${activeStats.refund_count}`} tone="text-zinc-300" />
              <div className="hidden sm:block h-6 w-px bg-zinc-800" />
              <BreakdownItem
                label="Notify delivered"
                value={`${activeStats.notify_success_count}`}
                tone="text-emerald-400"
                icon={BellRing}
              />
              <BreakdownItem
                label="Notify failed"
                value={`${activeStats.notify_fail_count}`}
                tone="text-red-400"
              />
              <BreakdownItem
                label="Notify pending"
                value={`${activeStats.notify_pending_count}`}
                tone="text-zinc-400"
              />
              <div className="sm:ml-auto text-xs text-zinc-500">
                Notify success rate{' '}
                <span className="text-zinc-200 font-medium">
                  {formatRate(activeStats.notify_success_rate)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm">
          No data for {activeCurrency} in this period
        </div>
      )}

      {/* Trend */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl">
        <div className="flex flex-col gap-4 p-5 border-b border-zinc-800">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-zinc-500" />
              <h2 className="text-base font-semibold text-white">Trend</h2>
              {loading && <Loader2 size={14} className="text-zinc-500 animate-spin" />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-lg bg-zinc-800/80 border border-zinc-700 p-0.5">
                {METRICS.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setMetric(item.key)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      metric === item.key
                        ? 'bg-zinc-700 text-white'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center rounded-lg bg-zinc-800/80 border border-zinc-700 p-0.5">
                {GRANULARITIES.map((item) => {
                  const disabled = item.key === 'hour' && hourDisabled;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setGran(item.key)}
                      disabled={disabled}
                      title={disabled ? 'Hour granularity supports up to 31 days' : undefined}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        gran === item.key
                          ? 'bg-zinc-700 text-white'
                          : disabled
                            ? 'text-zinc-600 cursor-not-allowed'
                            : 'text-zinc-400 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="p-5">
          {isFirstLoading ? (
            <div className="h-64 rounded-lg bg-zinc-800/40 animate-pulse" />
          ) : (
            <TrendChart
              labels={trendData.labels}
              series={trendData.series}
              valueFormatter={valueFormatter}
              axisFormatter={axisFormatter}
              labelFormatter={(label) => formatBucket(label, gran)}
            />
          )}
        </div>
      </div>

      {/* Channels */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 p-5 border-b border-zinc-800">
          <CreditCard size={18} className="text-zinc-500" />
          <h2 className="text-base font-semibold text-white">Channels</h2>
        </div>
        {loading && channels.length === 0 ? (
          <div className="p-5 space-y-3">
            {[0, 1].map((item) => (
              <div key={item} className="h-14 rounded-lg bg-zinc-800/40 animate-pulse" />
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">No channel data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-400 text-xs font-medium px-5 py-3">Channel</th>
                  <th className="text-left text-zinc-400 text-xs font-medium px-5 py-3">Currency</th>
                  <th className="text-right text-zinc-400 text-xs font-medium px-5 py-3">Orders</th>
                  <th className="text-right text-zinc-400 text-xs font-medium px-5 py-3">Paid</th>
                  <th className="text-right text-zinc-400 text-xs font-medium px-5 py-3">
                    Success amount
                  </th>
                  <th className="text-left text-zinc-400 text-xs font-medium px-5 py-3 w-48">
                    Success rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((item) => (
                  <tr
                    key={`${item.channel}-${item.currency}`}
                    className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/40 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-indigo-400/10 text-indigo-300">
                          <CreditCard size={15} />
                        </div>
                        <span className="text-white text-sm">{item.channel_text || item.channel}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs text-zinc-300 bg-zinc-800 px-2 py-1 rounded-md">
                        {item.currency}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-zinc-300 text-sm">{item.order_count}</td>
                    <td className="px-5 py-4 text-right text-zinc-300 text-sm">{item.success_count}</td>
                    <td className="px-5 py-4 text-right text-white text-sm font-medium">
                      {formatMoney(item.success_amount, item.currency)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-400"
                            style={{ width: `${Math.min(100, item.success_rate * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-zinc-300 w-11 text-right">
                          {formatRate(item.success_rate)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

interface BreakdownItemProps {
  label: string;
  value: string;
  tone?: string;
  icon?: React.ElementType;
}

const BreakdownItem: React.FC<BreakdownItemProps> = ({ label, value, tone, icon: Icon }) => (
  <div className="flex items-center gap-2">
    {Icon && <Icon size={14} className="text-zinc-500" />}
    <span className="text-zinc-500">{label}</span>
    <span className={`font-medium ${tone || 'text-zinc-200'}`}>{value}</span>
  </div>
);

export default PayReportPage;
